import { useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { App as CapacitorApp } from '@capacitor/app'
import { Capacitor } from '@capacitor/core'

/**
 * useDeepLink
 *
 * Listens for Universal Link / App Link opens (iOS and Android) and navigates
 * to the correct in-app route.
 *
 * Capacitor fires the `appUrlOpen` event when the app is opened from a
 * trybeup.com link. The event carries the full URL, e.g.:
 *   https://trybeup.com/profile/abc123
 *
 * We extract the path (and query string) and push it through React Router.
 *
 * Supported deep link routes:
 *   /profile/:userId      — public profiles
 *   /trybe/:id            — Trybe invite links
 *   /habits               — from milestone/reminder emails
 *   /duel/:id             — duel invites
 *   /?ref=:code           — referral signup links
 */
export function useDeepLink() {
  const navigate = useNavigate()

  useEffect(() => {
    // Only register the listener on native platforms.
    // On web this is a no-op — browser handles URLs natively.
    if (!Capacitor.isNativePlatform()) return
    // Stale-shell rule: OTA JS runs inside native shells of any age, and a
    // shell without the App plugin rejects addListener (the 2026-08-04
    // back-button crash class). Degrade to no deep links, not a crash.
    if (!Capacitor.isPluginAvailable('App')) return

    const listener = CapacitorApp.addListener('appUrlOpen', (event) => {
      try {
        const url = new URL(event.url)
        // Path + query only. Do NOT forward the hash: nothing consumes it
        // after boot (GoTrue's detectSessionInUrl and the manual recovery in
        // useAuth both run once, above this listener, at init) — so a token
        // in the fragment can't log anyone in here, but navigating with it
        // would write access_token/refresh_token into the SPA URL, which
        // PostHog ($current_url) and Sentry (navigation breadcrumbs) then
        // ship to third parties (audit 2026-08-06). Drop it.
        const destination = url.pathname + url.search
        if (destination && destination !== '/') {
          navigate(destination, { replace: false })
        }
      } catch (err) {
        // Malformed deep link URL — ignore silently
      }
    })

    // Clean up listener on unmount
    return () => {
      listener.then((l) => l.remove())
    }
  }, [navigate])
}
